import React, {useState, useEffect, useRef} from 'react'
import {useParams, useHistory} from 'react-router-dom'
import {DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, pointerWithin, closestCenter} from '@dnd-kit/core'
import DeckPanel from './DeckPanel'

// Classic tier-list rows. Colors run hot -> cold, top -> bottom.
const TIERS = [
  {id: 'S', label: 'S', color: '#ff7f7f'},
  {id: 'A', label: 'A', color: '#ffbf7f'},
  {id: 'B', label: 'B', color: '#ffdf80'},
  {id: 'C', label: 'C', color: '#ffff7f'},
  {id: 'D', label: 'D', color: '#7fdf7f'},
]

// Droppable ids that are ROWS (tiers), vs tiles (card ids, which start with 'c'). Used to prefer a
// tile the pointer is over (for precise before/after insertion) and fall back to the row otherwise.
const TIER_IDS = new Set([...TIERS.map(t => t.id), 'pool'])
const tierCollision = args => {
  let within = pointerWithin(args)
  let tile = within.find(x => !TIER_IDS.has(String(x.id)))
  if (tile) return [tile]
  let row = within.find(x => TIER_IDS.has(String(x.id)))
  if (row) return [row]
  return closestCenter(args)
}

const c = {
  bg: '#1a1a1a',
  panel: '#333',
  panel2: '#444',
  text: '#fff',
  textDim: '#888',
  accent: '#4da3ff',
}

const KEY = 'blogTierList'

let load = () => {
  try {
    let raw = localStorage.getItem(KEY)
    if (raw) return JSON.parse(raw)
  } catch (e) {}
  return []
}

// The art crop (the top-of-card art) for either a normal or double-faced card.
let getArt = card => card.image_uris?.art_crop || card.card_faces?.[0]?.image_uris?.art_crop || null

// One card = a square box, its background is the card's art crop. Draggable + clickable.
let CardBox = ({card, selected, anySelected, onSelect, readOnly}) => {
  const {attributes, listeners, setNodeRef: setDragRef, isDragging} = useDraggable({id: card.id, disabled: readOnly})
  const {setNodeRef: setDropRef} = useDroppable({id: card.id, disabled: readOnly})
  const [hover, setHover] = useState(false)
  return (
    <div
      ref={el => { setDragRef(el); setDropRef(el) }}
      {...listeners}
      {...attributes}
      onClick={() => onSelect(selected ? null : card.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={card.name}
      style={{
        ...styles.card,
        background: card.art ? `#000 url(${card.art}) center/cover` : card.color,
        opacity: isDragging ? 0.3 : anySelected && !selected ? 0.5 : 1,
        border: selected ? `2px solid ${c.accent}` : '2px solid transparent',
      }}>
      {/* Name hidden by default (art usually IDs the commander); fades in on hover. */}
      <span style={{...styles.cardName, opacity: hover ? 1 : 0}}>{card.name}</span>
    </div>
  )
}

// A tier row (or the unranked tray) — a drop target that lays cards out in a wrapping strip.
let TierRow = ({id, label, color, items, selectedId, onSelect, thin, readOnly}) => {
  const {setNodeRef, isOver} = useDroppable({id, disabled: readOnly})
  return (
    <div style={styles.row}>
      <div style={{...styles.rowLabel, ...(thin ? styles.rowLabelThin : null), background: color}}>{label}</div>
      <div ref={setNodeRef} style={{...styles.rowDrop, background: isOver ? '#2a2a2a' : c.bg}}>
        {items.map(item =>
          item.__ph ? (
            <div key="__ph" style={styles.cardPlaceholder} />
          ) : (
            <CardBox key={item.id} card={item} selected={item.id === selectedId} anySelected={selectedId != null} onSelect={onSelect} readOnly={readOnly} />
          ),
        )}
      </div>
    </div>
  )
}

let Blog = () => {
  const {slug} = useParams()
  const history = useHistory() // SPA nav back to the homepage (no full reload)
  const viewSlug = slug || null // present → viewing someone's shared tier list (read-only)
  const viewMode = !!viewSlug
  const [cards, setCards] = useState(() => (viewSlug ? [] : load()))
  const [viewData, setViewData] = useState(null) // {displayName, username, decks, owner, public} in view mode
  const [viewError, setViewError] = useState(null) // 'private' | 'not-found' | 'bad-link'
  const [share, setShare] = useState(null) // owner's own {slug, public} for the Share row
  const [shareOpen, setShareOpen] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)
  // Focus mode: opening a deck from the homepage sets blogDeckFocus='1' → collapse the tier list to a thin
  // rail so the deck editor is the focus. Toggling the rail handle persists the choice.
  const [focusMode, setFocusMode] = useState(() => { try { return localStorage.getItem('blogDeckFocus') === '1' } catch (e) { return false } })
  let setFocus = on => { setFocusMode(on); try { localStorage.setItem('blogDeckFocus', on ? '1' : '0') } catch (e) {} }
  const [selectedId, setSelectedId] = useState(null)
  const [selectTick, setSelectTick] = useState(0)
  const [activeId, setActiveId] = useState(null)
  const [dropTarget, setDropTarget] = useState(null) // {tier, index} — where the dragged tile will land
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [deckArtOver, setDeckArtOver] = useState(false) // dragging the deck-panel portrait over the tier list
  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 5}}))
  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400)
  let wide = winW >= 700

  // On load, if a deck is still active on the right (persisted in blogActiveDeckId), re-select its
  // linked tier-list tile on the left so its primer shows again — the deck restores itself, but the
  // tile selection (which drives the primer) didn't.
  useEffect(() => {
    document.title = 'Tier List'
    try {
      let deckId = localStorage.getItem('blogActiveDeckId')
      if (!deckId) return
      let tile = cards.find(x => x.deckId === deckId)
      if (tile) setSelectedId(tile.id)
    } catch (e) {}
  }, [])

  // Resizable deck rail: drag the divider to set its width; cards auto-scale + reflow (see DeckPanel).
  // Clamp lets the rail shrink to ~one card column (200px) and always leaves ≥340px for the tier list.
  const clampDeckW = w => Math.max(200, Math.min(w, Math.max(200, window.innerWidth - 340)))
  const [deckW, setDeckW] = useState(() => {
    try {
      let v = parseInt(localStorage.getItem('blogDeckWidth') || '', 10)
      if (v > 0) return v
    } catch (e) {}
    let w = typeof window !== 'undefined' ? window.innerWidth : 1400
    return Math.round(w * 0.44)
  })
  const startResize = e => {
    e.preventDefault()
    const onMove = ev => setDeckW(clampDeckW(window.innerWidth - ev.clientX))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Track viewport width so the layout can collapse to one column on narrow screens,
  // and keep the deck rail within bounds when the window resizes.
  useEffect(() => {
    let onResize = () => {
      setWinW(window.innerWidth)
      setDeckW(w => clampDeckW(w))
    }
    onResize() // clamp a stored width to the current viewport on mount
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Persist the deck rail width across reloads.
  useEffect(() => {
    try {
      localStorage.setItem('blogDeckWidth', String(deckW))
    } catch (e) {}
  }, [deckW])

  // Autosave to the browser on every change (own mode only). Also push to the server (debounced) so the
  // shared copy stays current — skipping the initial mount so we never clobber the server with stale local data.
  const didServerSync = useRef(false)
  useEffect(() => {
    if (viewMode) return
    try { localStorage.setItem(KEY, JSON.stringify(cards)) } catch (e) {}
    if (!didServerSync.current) { didServerSync.current = true; return }
    let t = setTimeout(() => {
      fetch('/api/tierlist', {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({tierlist: cards})}).catch(() => {})
    }, 800)
    return () => clearTimeout(t)
  }, [cards, viewMode])

  // View mode: fetch the shared tier list (tiles + primers + decks) by slug; render read-only.
  useEffect(() => {
    if (!viewSlug) return
    let cancelled = false
    ;(async () => {
      try {
        let res = await fetch(`/api/tierlist/view/${encodeURIComponent(viewSlug)}`, {credentials: 'include'})
        let data = await res.json()
        if (cancelled) return
        if (data.ok) {
          setCards(Array.isArray(data.tierlist) ? data.tierlist : [])
          setViewData({displayName: data.displayName, username: data.username, decks: data.decks || [], owner: !!data.owner, public: data.public})
          setViewError(null)
        } else {
          setViewError(data.reason || 'not-found')
        }
      } catch (e) {
        if (!cancelled) setViewError('not-found')
      }
    })()
    return () => { cancelled = true }
  }, [viewSlug])

  // Debounced fuzzy card search against Scryfall.
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    let t = setTimeout(async () => {
      try {
        let res = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(query)}&order=name&unique=cards`)
        let data = res.ok ? await res.json() : {data: []}
        if (!cancelled) setResults((data.data || []).slice(0, 8))
      } catch (e) {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query])

  let selected = cards.find(card => card.id === selectedId)
  let byTier = tier => cards.filter(card => card.tier === tier)
  // When the tier-list column gets narrow (deck rail dragged wide, or small screen), shrink the
  // rank labels to a thin sliver — just wide enough for the letter — so rows keep their room.
  let thinTiers = (wide ? winW - deckW - 30 : winW) < 600

  // Picking a search result creates a square with that card's art.
  let pickCard = card => {
    let id = 'c' + Date.now()
    setCards(cs => [...cs, {id, name: card.name, tier: 'pool', writeup: '', color: c.panel2, art: getArt(card)}])
    setSelectedId(id)
    // The new tile has no deck yet — bump the tick so the deck panel drops whatever
    // deck was open and shows this tile's empty/import state instead.
    setSelectTick(t => t + 1)
    setQuery('')
    setResults([])
  }

  let patch = (id, fields) => setCards(cs => cs.map(card => (card.id === id ? {...card, ...fields} : card)))

  let remove = id => {
    setCards(cs => cs.filter(card => card.id !== id))
    if (selectedId === id) setSelectedId(null)
  }

  // While dragging: figure out which tier + index the tile would drop into, so we can show a blank
  // placeholder there. Over a tile → before/after it (by pointer side); over a row's empty area → end.
  let onDragOver = ({active, over}) => {
    if (!over) { setDropTarget(null); return }
    let overId = String(over.id)
    let overCard = cards.find(x => x.id === over.id)
    let tier = overCard ? overCard.tier : overId
    let list = cards.filter(x => x.tier === tier && x.id !== active.id) // tiles in target tier, minus the dragged one
    if (!overCard) { setDropTarget({tier, index: list.length}); return }
    let idx = list.findIndex(x => x.id === over.id)
    if (idx < 0) idx = list.length
    let side = 0
    let ar = active.rect?.current?.translated
    if (ar && over.rect) side = ar.left + ar.width / 2 > over.rect.left + over.rect.width / 2 ? 1 : 0
    setDropTarget({tier, index: idx + side})
  }
  // The rendered contents of a tier row: its cards, minus the one being dragged, with a placeholder
  // marker spliced in at the drop index (so the row reflows/wraps to preview where the tile lands).
  let displayList = tier => {
    let list = cards.filter(x => x.tier === tier)
    if (activeId) list = list.filter(x => x.id !== activeId)
    if (dropTarget && dropTarget.tier === tier) {
      let arr = [...list]
      arr.splice(Math.max(0, Math.min(dropTarget.index, arr.length)), 0, {__ph: true, id: '__ph'})
      return arr
    }
    return list
  }
  let onDragEnd = ({active, over}) => {
    let target = dropTarget
    setActiveId(null)
    setDropTarget(null)
    if (!over) return
    setCards(cs => {
      let moving = cs.find(x => x.id === active.id)
      if (!moving) return cs
      let overCard = cs.find(x => x.id === over.id)
      let tier = target ? target.tier : overCard ? overCard.tier : String(over.id)
      let rest = cs.filter(x => x.id !== active.id)
      let updated = {...moving, tier}
      let tierItems = rest.filter(x => x.tier === tier)
      let idx = target ? Math.max(0, Math.min(target.index, tierItems.length)) : tierItems.length
      if (tierItems.length === 0) return [...rest, updated] // first/only tile in this tier
      if (idx >= tierItems.length) {
        let pos = rest.findIndex(x => x.id === tierItems[tierItems.length - 1].id) + 1
        return [...rest.slice(0, pos), updated, ...rest.slice(pos)]
      }
      let pos = rest.findIndex(x => x.id === tierItems[idx].id)
      return [...rest.slice(0, pos), updated, ...rest.slice(pos)]
    })
  }

  let activeCard = cards.find(card => card.id === activeId)

  // Link an imported deck to the selected tier-list tile, or spawn a new tile from its commander.
  let onLinkDeck = (deckId, commander) => {
    if (selectedId) patch(selectedId, {deckId})
  }
  let onAddCommanderTile = commander => {
    let id = 'c' + Date.now()
    setCards(cs => [...cs, {id, name: commander.name, tier: 'pool', writeup: '', color: c.panel2, art: commander.url || commander.scryfallUrl || null, deckId: commander.deckId}])
    setSelectedId(id)
  }
  // Bump on every tile click (even the already-selected one) so its deck re-loads.
  let selectCard = id => {
    setSelectedId(id)
    setSelectTick(t => t + 1)
  }
  let deckProps = {linkDeckId: selected?.deckId || null, linkTick: selectTick, hasSelectedTile: !!selectedId, selectedTile: selected || null, onLinkDeck, onAddCommanderTile, readOnly: viewMode, viewDecks: viewData?.decks || null}

  // Share popover (own tier list): fetch my slug + privacy, copy the link, toggle public/private.
  let openShare = async () => {
    setShareOpen(true)
    try {
      let res = await fetch('/api/tierlist/mine', {credentials: 'include'})
      let data = await res.json()
      if (data.loggedIn) setShare({slug: data.slug, public: data.public})
      else setShare({loggedIn: false})
    } catch (e) { setShare({error: true}) }
  }
  let shareUrl = share?.slug ? `${window.location.origin}/tierlist/${share.slug}` : ''
  let copyShare = () => { if (!shareUrl) return; navigator.clipboard?.writeText(shareUrl).catch(() => {}); setShareCopied(true); setTimeout(() => setShareCopied(false), 1500) }
  let setPublic = async isPublic => {
    setShare(s => ({...s, public: isPublic}))
    try {
      let res = await fetch('/api/tierlist/settings', {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({public: isPublic})})
      let data = await res.json()
      if (data.slug) setShare({slug: data.slug, public: data.public})
    } catch (e) {}
  }

  // Drag the deck-panel portrait (native HTML5 DnD) onto the tier list to add that deck as a tile
  // (if it isn't already present). Coexists with the dnd-kit tile reordering, which is pointer-based.
  let onDeckArtDragOver = e => {
    if (viewMode) return
    if (!e.dataTransfer.types.includes('application/x-moxdeck')) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!deckArtOver) setDeckArtOver(true)
  }
  let onDeckArtDrop = e => {
    if (viewMode) return
    let raw = e.dataTransfer.getData('application/x-moxdeck')
    setDeckArtOver(false)
    if (!raw) return
    e.preventDefault()
    let payload
    try { payload = JSON.parse(raw) } catch (err) { return }
    if (!payload || !payload.deckId) return
    let existing = cards.find(x => x.deckId === payload.deckId)
    if (existing) { selectCard(existing.id); return } // already in the tier list → just select it
    onAddCommanderTile({name: payload.name || 'Deck', url: payload.art || null, deckId: payload.deckId})
  }

  let collapsed = wide && focusMode && !viewMode // tier list collapsed to a rail; deck editor fills the width

  return (
    <div className="thinScroll" style={wide ? styles.pageWide : styles.page}>
      {collapsed ? (
        <div style={styles.focusRail}>
          <a className="blueHover" href="/" onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); history.push('/') }} style={{...styles.homeBtn, marginBottom: 0}} title="Home">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 9.5V21h14V9.5" />
            </svg>
          </a>
          <button className="blueHover" style={styles.focusHandle} onClick={() => setFocus(false)} title="Show tier list">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
              <rect x="2" y="2" width="6" height="6" rx="1" />
              <rect x="2" y="9" width="6" height="6" rx="1" />
              <rect x="9" y="9" width="6" height="6" rx="1" />
              <rect x="2" y="16" width="6" height="6" rx="1" />
              <rect x="9" y="16" width="6" height="6" rx="1" />
              <rect x="16" y="16" width="6" height="6" rx="1" />
            </svg>
          </button>
        </div>
      ) : (
      <div style={wide ? styles.scrollArea : styles.scrollAreaNarrow}>
        <div style={{...styles.layout, flexDirection: wide ? 'row' : 'column'}}>
          <div
            style={deckArtOver ? {...styles.center, outline: `2px dashed ${c.accent}`, outlineOffset: -4, borderRadius: 8} : styles.center}
            onDragOver={onDeckArtDragOver}
            onDrop={onDeckArtDrop}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDeckArtOver(false) }}>
            <div style={{display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center'}}>
              <a className="blueHover" href="/" onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); history.push('/') }} style={{...styles.homeBtn, marginBottom: 0}} title="Home">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 10.5 12 3l9 7.5" />
                  <path d="M5 9.5V21h14V9.5" />
                </svg>
              </a>
              <span
                className={wide && !viewMode ? 'blueHover' : undefined}
                style={{...styles.homeBtn, marginBottom: 0, borderColor: c.accent, color: c.accent, cursor: wide && !viewMode ? 'pointer' : 'default'}}
                onClick={wide && !viewMode ? () => setFocus(true) : undefined}
                title={wide && !viewMode ? 'Collapse tier list (focus the deck)' : 'Tier List (current page)'}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
                  <rect x="2" y="2" width="6" height="6" rx="1" />
                  <rect x="2" y="9" width="6" height="6" rx="1" />
                  <rect x="9" y="9" width="6" height="6" rx="1" />
                  <rect x="2" y="16" width="6" height="6" rx="1" />
                  <rect x="9" y="16" width="6" height="6" rx="1" />
                  <rect x="16" y="16" width="6" height="6" rx="1" />
                </svg>
              </span>
              {viewMode ? (
                <div style={{marginLeft: 10, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8}}>
                  <span style={{color: c.textDim, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    Viewing <b style={{color: c.text}}>{viewData?.displayName || '…'}</b>'s tier list
                  </span>
                  {viewData?.owner && <span style={{color: c.accent, fontSize: 11, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '2px 6px', whiteSpace: 'nowrap'}}>your share</span>}
                </div>
              ) : (
                <>
                  {/* Share your tier list — toggles the inline share row below the top row */}
                  <button className="blueHover" style={{...styles.homeBtn, width: 'auto', padding: '0 10px', fontSize: 13, marginBottom: 0, marginLeft: 'auto', ...(shareOpen ? {borderColor: c.accent, color: c.accent} : {})}} title="Share your tier list" onClick={() => { if (shareOpen) setShareOpen(false); else openShare() }}>Share</button>
                  {/* Card search — on the top row, right-aligned; + adds the top result to the Unranked tray */}
                  <div style={{...styles.topSearch, maxWidth: 'none', width: 220, minWidth: 0, marginBottom: 0}}>
                    <div style={{display: 'flex', gap: 6}}>
                      <input style={{...styles.searchInput, flex: 1, minWidth: 0, width: 'auto'}} value={query} placeholder="search…"
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && results[0]) pickCard(results[0]) }} />
                      <button className="blueHover" style={{...styles.searchSend, fontSize: 18, lineHeight: 1}} title="Add the top result" onClick={() => { if (results[0]) pickCard(results[0]) }}>+</button>
                    </div>
                    {(results.length > 0 || (searching && query.trim())) && (
                      <div style={styles.results}>
                        {searching && results.length === 0 && <div style={styles.resultEmpty}>Searching...</div>}
                        {results.map(card => (
                          <div key={card.id} style={styles.result} onClick={() => pickCard(card)}>
                            {getArt(card) && <div style={{...styles.resultThumb, backgroundImage: `url(${getArt(card)})`}} />}
                            <div style={styles.resultText}>
                              <div style={styles.resultName}>{card.name}</div>
                              <div style={styles.resultSet}>
                                {(card.set_name || card.set || '').toString()} {card.mana_cost ? `· ${card.mana_cost}` : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {!viewMode && shareOpen && (
              <div style={styles.shareRow}>
                {share == null ? (
                  <span style={{color: c.textDim, fontSize: 12}}>Loading…</span>
                ) : share.loggedIn === false ? (
                  <span style={{color: c.textDim, fontSize: 12}}>Log in (open one of your decks) to get a shareable link.</span>
                ) : share.error ? (
                  <span style={{color: '#ff6b6b', fontSize: 12}}>Couldn't load your link.</span>
                ) : (
                  <>
                    <input readOnly value={shareUrl} onFocus={e => e.target.select()} style={{...styles.searchInput, flex: 1, minWidth: 0, fontSize: 13}} />
                    <button className="blueHover" style={{...styles.searchSend, width: 'auto', padding: '0 14px', fontSize: 13, flexShrink: 0, ...(shareCopied ? {borderColor: c.accent, color: c.accent} : {})}} onClick={copyShare}>{shareCopied ? 'Copied' : 'Copy'}</button>
                    <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.textDim, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer'}} title={share.public !== false ? 'Anyone with the link can view' : 'Link disabled — only you can view'}>
                      <input type="checkbox" checked={share.public !== false} onChange={e => setPublic(e.target.checked)} />
                      Public
                    </label>
                  </>
                )}
              </div>
            )}

            {viewMode && viewError && (
              <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: c.textDim, textAlign: 'center', padding: 40, gap: 8}}>
                <div style={{fontSize: 16, color: c.text}}>{viewError === 'private' ? 'This tier list is private.' : 'Tier list not found.'}</div>
                <div style={{fontSize: 13}}>{viewError === 'private' ? 'The owner has turned off sharing for this link.' : 'The link may be broken, or the share token was regenerated.'}</div>
                <a className="blueHover" href="/tierlist" style={{...styles.homeBtn, width: 'auto', padding: '0 12px', marginBottom: 0, textDecoration: 'none'}}>Go to your tier list</a>
              </div>
            )}

        <DndContext sensors={sensors} collisionDetection={tierCollision} onDragStart={({active}) => setActiveId(active.id)} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => { setActiveId(null); setDropTarget(null) }}>
          <div style={styles.board}>
            {TIERS.map(t => (
              <TierRow key={t.id} id={t.id} label={t.label} color={t.color} items={displayList(t.id)} selectedId={selectedId} onSelect={selectCard} thin={thinTiers} readOnly={viewMode} />
            ))}
          </div>

          {/* Unranked tray — sits just under D, no label */}
          <div style={{height: 8}} />
          <TierRow id="pool" label="" color={c.panel} items={displayList('pool')} selectedId={selectedId} onSelect={selectCard} thin={thinTiers} readOnly={viewMode} />

          <DragOverlay>
            {activeCard ? (
              <div style={{...styles.card, background: activeCard.art ? `#000 url(${activeCard.art}) center/cover` : activeCard.color, cursor: 'grabbing'}}>
                <span style={styles.cardName}>{activeCard.name}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Primer for the selected card */}
        <div style={wide ? {...styles.primer, minHeight: 'calc(100vh - 80px)'} : styles.primer}>
          {selected ? (
            <>
              <input style={styles.nameInput} value={selected.name} readOnly={viewMode} onChange={viewMode ? undefined : e => patch(selected.id, {name: e.target.value})} onFocus={e => (e.target.style.borderColor = c.accent)} onBlur={e => (e.target.style.borderColor = c.panel2)} />
              <textarea
                style={styles.writeup}
                value={selected.writeup}
                placeholder={viewMode ? '' : 'primer'}
                readOnly={viewMode}
                onChange={viewMode ? undefined : e => patch(selected.id, {writeup: e.target.value})}
              />
              {!viewMode && (
                <div style={styles.primerFooter}>
                  <span style={{color: c.textDim, fontSize: 12}}>Saved automatically</span>
                  <button style={styles.deleteBtn} onClick={() => remove(selected.id)}>
                    Delete
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textDim, textAlign: 'center', padding: 40}}>{viewMode ? 'Click a tile to read its primer.' : 'Search a card above, then click it to write its primer.'}</div>
          )}
        </div>
        </div>
        {!wide && (
          <div style={styles.rightRailNarrow}>
            <DeckPanel {...deckProps} />
          </div>
        )}
        </div>
      </div>
      )}
      {wide && (
        <>
          {!collapsed && (
            <div
              style={styles.divider}
              onMouseDown={startResize}
              onMouseEnter={e => (e.currentTarget.style.background = c.accent)}
              onMouseLeave={e => (e.currentTarget.style.background = c.panel2)}
              title="Drag to resize the deck panel"
            />
          )}
          <div style={collapsed ? {...styles.deckDock, flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0} : {...styles.deckDock, width: deckW}}>
            <DeckPanel {...deckProps} />
          </div>
        </>
      )}
    </div>
  )
}

let styles = {
  page: {height: '100vh', overflowY: 'auto', scrollbarGutter: 'stable', WebkitOverflowScrolling: 'touch', background: c.bg, color: c.text, fontFamily: 'system-ui, sans-serif'},
  pageWide: {height: '100vh', display: 'flex', flexDirection: 'row', overflow: 'hidden', background: c.bg, color: c.text, fontFamily: 'system-ui, sans-serif'},
  homeBtn: {display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 32, boxSizing: 'border-box', background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, textDecoration: 'none', borderRadius: 3, marginBottom: 12},
  scrollArea: {flex: 1, height: '100vh', overflowY: 'auto', scrollbarGutter: 'stable', WebkitOverflowScrolling: 'touch', minWidth: 0},
  scrollAreaNarrow: {},
  deckDock: {height: '100vh', flexShrink: 0, overflowY: 'auto', scrollbarGutter: 'stable', WebkitOverflowScrolling: 'touch', background: c.bg},
  divider: {width: 6, flexShrink: 0, cursor: 'col-resize', background: c.panel2, transition: 'background 0.15s ease'},
  focusRail: {width: 44, flexShrink: 0, height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '6px 0', borderRight: `1px solid ${c.panel2}`, background: c.bg},
  focusHandle: {display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 32, boxSizing: 'border-box', background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, cursor: 'pointer'},
  layout: {display: 'flex', gap: 24, padding: '4px 4px 24px 2px', alignItems: 'flex-start'},
  leftRail: {width: 280, flexShrink: 0},
  center: {flex: 1, maxWidth: 900, minWidth: 0},
  rightRailNarrow: {width: '100%', marginTop: 8},
  title: {fontSize: 22, fontWeight: 300, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 20px'},
  board: {display: 'flex', flexDirection: 'column', gap: 3, borderRadius: 3, overflow: 'hidden'},
  row: {display: 'flex', minHeight: 76, background: c.bg},
  rowLabel: {width: 72, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#1a1a1a'},
  rowLabelThin: {width: 24, fontSize: 15},
  rowDrop: {flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6, padding: '2px 8px', alignContent: 'flex-start'},
  card: {position: 'relative', width: 72, height: 72, borderRadius: 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', boxSizing: 'border-box', cursor: 'pointer', color: '#fff', userSelect: 'none', overflow: 'hidden', touchAction: 'none', transition: 'opacity 0.15s ease'},
  cardPlaceholder: {width: 72, height: 72, borderRadius: 3, boxSizing: 'border-box', border: `2px dashed ${c.accent}`, background: 'rgba(77,163,255,0.12)', flexShrink: 0},
  cardName: {position: 'absolute', left: 0, right: 0, bottom: 0, padding: '2px 3px', fontSize: 9, lineHeight: 1.15, textAlign: 'center', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', wordBreak: 'break-word', transition: 'opacity 0.1s ease'},
  topSearch: {position: 'relative', maxWidth: 380, marginBottom: 18, zIndex: 6},
  trayHeader: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 0 6px', position: 'relative', zIndex: 5},
  searchWrap: {position: 'relative', width: 280},
  searchInput: {width: '100%', boxSizing: 'border-box', background: c.panel, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '8px 12px', fontSize: 14},
  searchSend: {flexShrink: 0, width: 40, background: c.panel, color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'},
  results: {position: 'absolute', top: '100%', right: 0, left: 0, marginTop: 4, background: c.panel, border: `1px solid ${c.panel2}`, borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 320, overflowY: 'auto', zIndex: 10},
  result: {display: 'flex', alignItems: 'center', gap: 8, padding: 6, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)'},
  resultThumb: {width: 44, height: 32, flexShrink: 0, borderRadius: 3, backgroundSize: 'cover', backgroundPosition: 'center'},
  resultText: {overflow: 'hidden'},
  resultName: {fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
  resultSet: {fontSize: 11, color: c.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
  resultEmpty: {padding: 10, fontSize: 13, color: c.textDim},
  shareRow: {display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12},
  primer: {marginTop: 16, background: 'transparent', padding: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box'},
  nameInput: {width: '100%', boxSizing: 'border-box', background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, outline: 'none', padding: '10px 12px', fontSize: 16, marginBottom: 10},
  writeup: {width: '100%', boxSizing: 'border-box', flex: 1, minHeight: 160, background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, outline: 'none', padding: 12, fontSize: 14, lineHeight: 1.6, resize: 'none', fontFamily: 'inherit'},
  primerFooter: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10},
  deleteBtn: {background: 'transparent', color: '#ff6b6b', border: '1px solid #ff6b6b', borderRadius: 3, padding: '6px 12px', fontSize: 13, cursor: 'pointer'},
}

export default Blog
